/**
 * AdminAccountPage — /admin/account (SCRUM-182).
 *
 * The admin panel had no settings surface at all, so an admin had nowhere to
 * see or change their own name, email, password or signed-in devices. Under
 * the persona-isolation decision (2026-07-31) an admin must not be sent to the
 * consumer `/account/*` screens for this, so the surface lives inside the
 * `/admin/*` namespace and renders in the admin shell.
 *
 * It reuses the account API functions (`getMe` / `patchMe` / `getSessions` /
 * `revokeSession` / `revokeOtherSessions`) rather than re-implementing them —
 * these are the shared account endpoints for the one underlying user row
 * (ADR-035/043), not a consumer-persona surface.
 *
 * Password: deliberately NOT an in-page "new password" field. Changing it goes
 * through the emailed reset link, which is the flow the admin door already
 * points at (`AuthLoginScreen`'s set-password CTA) and avoids typing a new
 * credential into a session that is already authenticated.
 *
 * Email is read-only: no endpoint changes it, and inventing one is out of
 * scope for this ticket.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Card, Skeleton } from "@garden/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import {
  getMe,
  getSessions,
  patchMe,
  revokeOtherSessions,
  revokeSession,
  type SessionInfo,
} from "@/features/account/api";

function formatWhen(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function AdminAccountPage(): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const queryClient = useQueryClient();

  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe, staleTime: 60_000 });
  const sessionsQuery = useQuery({
    queryKey: ["admin", "account", "sessions"],
    queryFn: getSessions,
  });

  const [displayName, setDisplayName] = useState<string | null>(null);
  const nameValue = displayName ?? meQuery.data?.display_name ?? "";
  const nameInvalid = nameValue.trim().length === 0;

  const saveProfile = useMutation({
    mutationFn: () => patchMe({ display_name: nameValue.trim() }),
    onSuccess: () => {
      toast.success(t("account.saved"));
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: () => toast.error(t("account.saveError")),
  });

  const dropSession = useMutation({
    mutationFn: (familyId: string) => revokeSession(familyId),
    onSuccess: () => {
      toast.success(t("account.revoked"));
      void queryClient.invalidateQueries({ queryKey: ["admin", "account", "sessions"] });
    },
    onError: () => toast.error(t("account.revokeError")),
  });

  const dropOthers = useMutation({
    mutationFn: () => revokeOtherSessions(),
    onSuccess: () => {
      toast.success(t("account.revoked"));
      void queryClient.invalidateQueries({ queryKey: ["admin", "account", "sessions"] });
    },
    onError: () => toast.error(t("account.revokeError")),
  });

  const sessions: SessionInfo[] = sessionsQuery.data?.sessions ?? [];
  const others = sessions.filter((s) => !s.is_current);

  return (
    <div className="flex flex-col gap-6" data-testid="admin-account-page">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-[-0.01em]">{t("account.title")}</h1>
        <p className="text-sm text-[var(--g-ink-2)]">{t("account.lede")}</p>
      </header>

      {/* ── Profile ─────────────────────────────────────────────────────── */}
      <Card className="flex flex-col gap-4 p-5">
        <h2 className="text-base font-semibold">{t("account.profile")}</h2>

        {meQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="admin-account-name">{t("account.displayName")}</Label>
              <Input
                id="admin-account-name"
                value={nameValue}
                aria-required="true"
                aria-invalid={nameInvalid || undefined}
                aria-describedby={nameInvalid ? "admin-account-name-error" : undefined}
                data-testid="admin-account-name"
                onChange={(e) => setDisplayName(e.target.value)}
              />
              {nameInvalid ? (
                <p
                  id="admin-account-name-error"
                  role="alert"
                  data-testid="admin-account-name-error"
                  className="text-xs text-[var(--g-error)]"
                >
                  {t("account.nameRequired")}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="admin-account-email">{t("account.email")}</Label>
              <Input
                id="admin-account-email"
                value={meQuery.data?.email ?? ""}
                readOnly
                disabled
                data-testid="admin-account-email"
              />
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-sm text-[var(--g-ink-2)]">{t("account.roles")}</span>
              <span className="text-sm" data-testid="admin-account-roles">
                {(meQuery.data?.roles ?? []).length > 0
                  ? (meQuery.data?.roles ?? []).join(", ")
                  : t("account.noRoles")}
              </span>
            </div>

            <div>
              <Button
                onClick={() => saveProfile.mutate()}
                disabled={nameInvalid || saveProfile.isPending}
                data-testid="admin-account-save"
              >
                {t("account.save")}
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* ── Password ────────────────────────────────────────────────────── */}
      <Card className="flex flex-col gap-3 p-5">
        <h2 className="text-base font-semibold">{t("account.password")}</h2>
        <p className="text-sm text-[var(--g-ink-2)]">{t("account.passwordLede")}</p>
        <Link
          to="/forgot-password"
          data-testid="admin-account-password-link"
          className="text-sm font-semibold text-[var(--g-primary-ink)] underline-offset-4 hover:underline"
        >
          {t("account.passwordCta")} →
        </Link>
      </Card>

      {/* ── Sessions ────────────────────────────────────────────────────── */}
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold">{t("account.sessions")}</h2>
          <p className="text-sm text-[var(--g-ink-2)]">{t("account.sessionsLede")}</p>
        </div>

        {sessionsQuery.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : sessions.length === 0 ? (
          <p className="text-sm text-[var(--g-ink-3)]">{t("account.noSessions")}</p>
        ) : (
          <ul className="flex flex-col" data-testid="admin-account-sessions">
            {sessions.map((s) => (
              <li
                key={s.family_id}
                className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--g-hairline)] py-3 first:border-t-0"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm">
                    {s.user_agent ?? s.ip_masked ?? s.family_id.slice(0, 8)}
                    {s.is_current ? ` · ${t("account.current")}` : ""}
                  </span>
                  <span className="text-xs text-[var(--g-ink-3)]">
                    {t("account.lastSeen")}: {formatWhen(s.last_used_at, i18n.language)}
                  </span>
                </div>
                {!s.is_current ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dropSession.mutate(s.family_id)}
                    disabled={dropSession.isPending}
                    data-testid={`admin-account-revoke-${s.family_id}`}
                  >
                    {t("account.revoke")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {others.length > 0 ? (
          <div>
            <Button
              variant="ghost"
              onClick={() => dropOthers.mutate()}
              disabled={dropOthers.isPending}
              data-testid="admin-account-revoke-others"
            >
              {t("account.revokeAll")}
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

export default AdminAccountPage;
